// src/components/ui/AchievementToast.tsx - ИСПРАВЛЕННАЯ ВЕРСИЯ
// Фиксы:
// 1. z-index: 10000 (выше Navigation)
// 2. Автозакрытие 3.5 сек
// 3. Кнопка закрытия
// 4. Прогресс-бар
// 5. ✅ Иконки из lucide-react вместо emoji

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PartyPopper,
  Flame,
  Heart,
  Star,
  Sprout,
  Moon,
  Crown,
  Gem,
  Share2,
  Gift,
  MessageCircle,
  Sparkles,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { celebrateAchievement } from '../../utils/confetti';
import type { Achievement } from '../../data/achievementsData';

// ============= ICON MAP =============
const ICON_MAP: Record<string, LucideIcon> = {
  PartyPopper,
  Flame,
  Heart,
  Star,
  Sprout,
  Moon,
  Crown,
  Gem,
  Share2,
  Gift,
  MessageCircle,
  Sparkles,
  Trophy,
};

const ToastContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000; /* ✅ ИСПРАВЛЕНО: Выше всех элементов */
  pointer-events: none;
  width: calc(100% - 32px);
  max-width: 380px;
`;

const ToastCard = styled(motion.div)`
  pointer-events: all;
  background: linear-gradient(135deg,
    rgba(34, 197, 94, 0.98),
    rgba(16, 185, 129, 0.95)
  );
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 16px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 20px 40px rgba(34, 197, 94, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    background: linear-gradient(135deg,
      rgba(34, 197, 94, 1),
      rgba(16, 185, 129, 0.98)
    );
  }

  &:active {
    transform: scale(0.98);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(52, 211, 153, 0.95),
      rgba(34, 197, 94, 0.92)
    );
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow:
      0 20px 40px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    &:hover {
      background: linear-gradient(135deg,
        rgba(52, 211, 153, 1),
        rgba(34, 197, 94, 0.98)
      );
    }
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ProgressBar = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0 0 20px 20px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  /* ✅ Стили для lucide-react иконок */
  svg {
    color: #ffffff;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 24px;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const AchievementName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2px;
  line-height: 1.2;
`;

const Description = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
`;

interface AchievementToastProps {
  achievement: Achievement;
  onClose: () => void;
}

export const AchievementToast: React.FC<AchievementToastProps> = ({
  achievement,
  onClose
}) => {
  const { hapticFeedback } = useTelegramWebApp();
  const AUTO_CLOSE_TIME = 3500; // ✅ 3.5 секунды

  // ✅ Получаем компонент иконки из lucide-react
  const IconComponent = ICON_MAP[achievement.icon] || Star;

  useEffect(() => {
    // ✅ Конфетти
    celebrateAchievement();

    // ✅ Вибрация
    hapticFeedback('success');
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100, 50, 200]);
    }

    // ✅ Автозакрытие
    const timer = setTimeout(() => {
      onClose();
    }, AUTO_CLOSE_TIME);

    return () => clearTimeout(timer);
  },[hapticFeedback, onClose]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticFeedback('light');
    onClose();
  };

  return (
    <ToastContainer>
      <AnimatePresence>
        <ToastCard
          onClick={handleClose}
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25
          }}
        >
          {/* ✅ Кнопка закрытия */}
          <CloseButton
            onClick={handleClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Закрыть"
          >
            ✕
          </CloseButton>

          {/* ✅ ИСПРАВЛЕНО: Иконка из lucide-react */}
          <IconCircle>
            <IconComponent size={28} strokeWidth={2} />
          </IconCircle>
          <Content>
            <Title>🏆 Достижение открыто!</Title>
            <AchievementName>{achievement.name}</AchievementName>
            <Description>{achievement.description}</Description>
          </Content>

          {/* ✅ Прогресс-бар */}
          <ProgressBar
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: AUTO_CLOSE_TIME / 1000, ease: 'linear' }}
          />
        </ToastCard>
      </AnimatePresence>
    </ToastContainer>
  );
};